import { createSelector } from 'reselect';
import isEmpty from 'lodash/isEmpty';
import { getFormValues, isInvalid } from 'redux-form';
import { initialState } from './reducer';
import { ICEPACK_FORM_NAME, ICEPACK_FILLED_TABLE_ROWS_NAMES } from './constants';
import { thousandSeparator } from '../reqs-filter/utils';

export const selectIcepackFilledModalDomain = state => state.icepackFilledModalReducer || initialState;
export const selectRouterDomain = state => state.router || {};

export const getIsModalShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack_filled');
	});

export const getIsIcepackFilledAddFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack_filled' && query?.mode === 'add');
	});

export const getIsIcepackFilledEditFormOpenSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack_filled' && query?.mode === 'edit');
	});

export const getActiveIcepackFilledIdSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return query?.modal === 'icepack_filled' ? query?.id : null;
	});

export const getIsAuditTabShownSelector = () =>
	createSelector(selectRouterDomain, ({ location: { query } }) => {
		return Boolean(!isEmpty(query) && query?.modal === 'icepack_filled' && query?.id && query?.tab === 'audit');
	});

export const getIsIcepackFilledCreatingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isIcepackFilledCreating }) => isIcepackFilledCreating);

export const getIsIcepackFilledUpdatingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isIcepackFilledUpdating }) => isIcepackFilledUpdating);

export const getIsIcepackFilledDeletingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isIcepackFilledDeleting }) => isIcepackFilledDeleting);

export const getIcepacksOptionsSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ icepacks }) =>
		icepacks.map(icepack => ({
			key: icepack.id,
			value: icepack.id,
			text: icepack.name,
		})),
	);

export const getIsIcepacksLoadingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isIcepacksLoading }) => isIcepacksLoading);

export const getIcepacksFillersOptionsSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ icepacksFillers }) =>
		icepacksFillers.map(filler => ({
			key: filler.id,
			value: filler.id,
			text: filler.name,
		})),
	);

export const getIsIcepacksFillersLoadingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isIcepacksFillersLoading }) => isIcepacksFillersLoading);

export const getIcepacksStatesOptionsSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ icepacksStates }) =>
		icepacksStates.map(state => ({
			key: state.id,
			value: state.id,
			text: state.name,
		})),
	);

export const getIsIcepacksStatesLoadingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isIcepacksStatesLoading }) => isIcepacksStatesLoading);

export const getModesOptionsSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ modes }) =>
		modes.map(mode => ({
			key: mode.id,
			value: mode.id,
			text: mode.name_with_symbol,
		})),
	);

export const getIsModesLoadingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isModesLoading }) => isModesLoading);

export const getIsIcepackFilledInfoLoadingSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ isIcepackFilledInfoLoading }) => isIcepackFilledInfoLoading);

export const getIcepackFilledInfoSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ icepackFilledInfo }) => {
		if (!icepackFilledInfo) return [];

		return Object.entries(ICEPACK_FILLED_TABLE_ROWS_NAMES).reduce((result, [key, value]) => {
			if (key === 'partner') {
				result.push({
					id: key,
					title: value,
					value: icepackFilledInfo.icepack.partner.name,
				});

				return result;
			}

			if (key === 'filler') {
				result.push({
					id: key,
					title: value,
					value: icepackFilledInfo.filler.name,
				});

				return result;
			}

			if (key === 'state') {
				result.push({
					id: key,
					title: value,
					value: icepackFilledInfo.state.name,
				});

				return result;
			}

			if (key === 'mode') {
				result.push({
					id: key,
					title: value,
					value: icepackFilledInfo.mode.name_with_symbol,
				});

				return result;
			}

			if (key === 'weight') {
				result.push({
					id: key,
					title: value,
					value: `${thousandSeparator(icepackFilledInfo.weight)} кг`,
				});

				return result;
			}

			if (key === 'author') {
				result.push({
					id: key,
					title: value,
					value: {
						name: `${icepackFilledInfo?.user?.last_name || ''} ${
							icepackFilledInfo?.user?.first_name || ''
						}`,
						role: icepackFilledInfo?.user?.role?.name || '',
					},
				});

				return result;
			}

			result.push({
				id: key,
				title: value,
				value: icepackFilledInfo[key] ?? '-',
			});

			return result;
		}, []);
	});

export const getIcepackFilledNameSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ icepackFilledInfo }) => icepackFilledInfo?.icepack?.name ?? '');

export const getFormInitialValuesSelector = () =>
	createSelector(selectIcepackFilledModalDomain, ({ icepackFilledInfo }) => {
		if (!icepackFilledInfo) {
			return {};
		}

		return {
			...icepackFilledInfo,
			icepack_id: icepackFilledInfo.icepack?.id,
			filler_id: icepackFilledInfo.filler?.id,
			state_id: icepackFilledInfo.state?.id,
			mode_id: icepackFilledInfo.mode?.id,
		};
	});

export const getFormIsInvalidSelector = () => createSelector(isInvalid(ICEPACK_FORM_NAME), isInvalid => isInvalid);

export const getDataForSaveSelector = () => createSelector(getFormValues(ICEPACK_FORM_NAME), formValues => formValues);
